import { useState } from "react";
import { Box } from "@mui/material";
import theme from "../../theme";
import { GenerateInsightsForm } from "../../@components/GenerateInsight/GenerateInsightForm/GenerateInsightsForm";
import { GenerateInsightsData } from "../../@components/GenerateInsight/GenerateInsightData/GenerateInsightsData";
import { LoadingProgress } from "../../@components/ReusableComponents/LoadingProgress";
import { useAccountInfo } from "../../hooks/useAccountInfo";
import { useActivityTracking } from "../../hooks/userActivityTracking";

export function GenerateInsights() {
  // DISPLAY STATE - Controls what is shown on screen
  const [isFormOpen, setIsFormOpen] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  // LOADING PROGRESS STATE
  const [loadedCount, setLoadedCount] = useState(0);
  const [totalToLoad, setTotalToLoad] = useState(0);

  const { accountInfo } = useAccountInfo();

  // USER ACTIVITY TRACKING
  const { trackAction, resetSession, snapshotSession, restoreSession } =
    useActivityTracking("generate_insight", accountInfo?.accountId);

  // DATA STATES - All the data needed for GenerateInsightsData
  const [newsAffectingPlans, setNewsAffectingPlans] = useState("");
  const [sizeOfMarket, setSizeOfMarket] = useState("");
  const [keyChallengesAndTrends, setKeyChallengesAndTrends] = useState("");
  const [porterFiveForces, setPorterFiveForces] = useState("");
  const [generalNews, setGeneralNews] = useState([]);
  const [categorySpecificNews, setCategorySpecificNews] = useState("");
  const [costDrivers, setCostDrivers] = useState();
  const [suppliers, setSuppliers] = useState([]);
  const [accreditations, setAccreditations] = useState("");
  const [regulations, setRegulations] = useState("");
  const [suppliersToShow, setSuppliersToShow] = useState([]);
  const [existingSuppliers, setExistingSuppliers] = useState([]);
  const [globalSuppliers, setGlobalSuppliers] = useState([]);
  const [financialReports, setFinancialReports] = useState([]);
  const [taxonomyPath, setTaxonomyPath] = useState("");
  const [history, setHistory] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [currentEntryId, setCurrentEntryId] = useState(null);
  const [spendOverview, setSpendOverview] = useState(null);
  const [spendBySupplier, setSpendBySupplier] = useState(null);
  const [spendByDepartment, setSpendByDepartment] = useState(null);
  const [spendByBusinessUnit, setSpendByBusinessUnit] = useState(null);
  const [spendL3Insights, setSpendL3Insights] = useState(null);
  const [spendProcurementLevers, setSpendProcurementLevers] = useState(null);
  const [spendCostDrivers, setSpendCostDrivers] = useState(null);
  const [spendInflationOverview, setSpendInflationOverview] = useState(null);
  const [spendCommodityInflation, setSpendCommodityInflation] = useState(null);

  const [formValues, setFormValues] = useState({
    category: "",
    L2: "",
    L3: "",
    L4: "",
    sector: accountInfo?.sector ?? "",
    country: "",
    approximatespend: 0,
    context: "",
  });

  // CONDITIONAL RENDERING - What to show
  const showImageAndForm = isFormOpen && !isLoading && !isLoaded;
  const showLoading = isLoading;
  const showData = isLoaded;

  return (
    <Box sx={{ width: "100%" }}>
      {showImageAndForm && (
        <Box
          sx={{
            display: "flex",
            flexDirection: {
              xs: "column",
              sm: "column",
              md: "row",
            },
            alignItems: {
              xs: "center",
              sm: "center",
              md: "flex-start",
            },
            justifyContent: {
              xs: "center",
              sm: "center",
              md: "flex-start",
            },
            padding: 0,
            minHeight: { md: "100vh" },
          }}
        >
          <Box
            sx={{
              // Hide image on small screens (xs, sm)
              display: {
                xs: "none",
                sm: "none",
                md: "block",
                xl: "block",
              },
              width: {
                md: "55%",
                xl: "60%",
              },
              height: "100vh",
              m: 0,
              p: 0,
              flexShrink: 0,
            }}
          >
            <Box
              component="img"
              src="/assets/form1.png"
              alt="Form image"
              sx={{
                width: "100%",
                height: "100%",
                borderTopRightRadius: theme.borderRadius.xxxxl,
                borderBottomRightRadius: theme.borderRadius.xxxxl,
                borderTopLeftRadius: 0,
                borderBottomLeftRadius: 0,
                objectFit: "cover",
                boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
                margin: 0,
                display: "block",
              }}
            />
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              flex: 1,
              minHeight: "100vh",
              pr: {
                xs: 0,
                sm: 0,
                md: "0.5%",
                xl: "2%",
              },
            }}
          >
            <GenerateInsightsForm
              isFormOpen={isFormOpen}
              setIsFormOpen={setIsFormOpen}
              isLoading={isLoading}
              setIsLoading={setIsLoading}
              isLoaded={isLoaded}
              setIsLoaded={setIsLoaded}
              loadedCount={loadedCount}
              setLoadedCount={setLoadedCount}
              totalToLoad={totalToLoad}
              setTotalToLoad={setTotalToLoad}
              isGenerating={isGenerating}
              setIsGenerating={setIsGenerating}
              history={history}
              setHistory={setHistory}
              trackAction={trackAction}
              resetSession={resetSession}
              snapshotSession={snapshotSession}
              restoreSession={restoreSession}
              currentEntryId={currentEntryId}
              setCurrentEntryId={setCurrentEntryId}
              setNewsAffectingPlans={setNewsAffectingPlans}
              setSizeOfMarket={setSizeOfMarket}
              setKeyChallengesAndTrends={setKeyChallengesAndTrends}
              setPorterFiveForces={setPorterFiveForces}
              setGeneralNews={setGeneralNews}
              setCategorySpecificNews={setCategorySpecificNews}
              setCostDrivers={setCostDrivers}
              setSuppliers={setSuppliers}
              setAccreditations={setAccreditations}
              setRegulations={setRegulations}
              setSuppliersToShow={setSuppliersToShow}
              setExistingSuppliers={setExistingSuppliers}
              setGlobalSuppliers={setGlobalSuppliers}
              setFinancialReports={setFinancialReports}
              formValues={formValues}
              setFormValues={setFormValues}
              setTaxonomyPath={setTaxonomyPath}
              costDrivers={costDrivers}
              newsAffectingPlans={newsAffectingPlans}
              sizeOfMarket={sizeOfMarket}
              keyChallengesAndTrends={keyChallengesAndTrends}
              porterFiveForces={porterFiveForces}
              regulations={regulations}
              accreditations={accreditations}
              spendOverview={spendOverview}
              spendBySupplier={spendBySupplier}
              spendByDepartment={spendByDepartment}
              spendByBusinessUnit={spendByBusinessUnit}
              spendL3Insights={spendL3Insights}
              spendProcurementLevers={spendProcurementLevers}
              spendCostDrivers={spendCostDrivers}
              spendInflationOverview={spendInflationOverview}
              spendCommodityInflation={spendCommodityInflation}
              setSpendOverview={setSpendOverview}
              setSpendBySupplier={setSpendBySupplier}
              setSpendByBusinessUnit={setSpendByBusinessUnit}
              setSpendByDepartment={setSpendByDepartment}
              setSpendL3Insights={setSpendL3Insights}
              setSpendProcurementLevers={setSpendProcurementLevers}
              setSpendCostDrivers={setSpendCostDrivers}
              setSpendInflationOverview={setSpendInflationOverview}
              setSpendCommodityInflation={setSpendCommodityInflation}
            />
          </Box>
        </Box>
      )}
      {showLoading && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "100vh",
          }}
        >
          <LoadingProgress
            loadedCount={loadedCount}
            totalToLoad={totalToLoad}
          />
        </Box>
      )}
      <Box sx={{ display: "flex", justifyContent: "center" }}>
        {showData && (
          <GenerateInsightsData
            formValues={formValues}
            setFormValues={setFormValues}
            costDrivers={costDrivers}
            setCostDrivers={setCostDrivers}
            newsAffectingPlans={newsAffectingPlans}
            setNewsAffectingPlans={setNewsAffectingPlans}
            sizeOfMarket={sizeOfMarket}
            setSizeOfMarket={setSizeOfMarket}
            keyChallengesAndTrends={keyChallengesAndTrends}
            setKeyChallengesAndTrends={setKeyChallengesAndTrends}
            porterFiveForces={porterFiveForces}
            setPorterFiveForces={setPorterFiveForces}
            generalNews={generalNews}
            setGeneralNews={setGeneralNews}
            categorySpecificNews={categorySpecificNews}
            setCategorySpecificNews={setCategorySpecificNews}
            financialReports={financialReports}
            setFinancialReports={setFinancialReports}
            regulations={regulations}
            setRegulations={setRegulations}
            accreditations={accreditations}
            setAccreditations={setAccreditations}
            suppliers={suppliers}
            setSuppliers={setSuppliers}
            suppliersToShow={suppliersToShow}
            setSuppliersToShow={setSuppliersToShow}
            existingSuppliers={existingSuppliers}
            setExistingSuppliers={setExistingSuppliers}
            globalSuppliers={globalSuppliers}
            setGlobalSuppliers={setGlobalSuppliers}
            setIsLoading={setIsLoading}
            setIsLoaded={setIsLoaded}
            setLoadedCount={setLoadedCount}
            setTotalToLoad={setTotalToLoad}
            selectedL3={formValues.L3}
            taxonomyPath={taxonomyPath}
            isFormOpen={isFormOpen}
            setIsFormOpen={setIsFormOpen}
            isLoaded={isLoaded}
            setTaxonomyPath={setTaxonomyPath}
            history={history}
            setHistory={setHistory}
            trackAction={trackAction}
            snapshotSession={snapshotSession}
            restoreSession={restoreSession}
            resetSession={resetSession}
            isGenerating={isGenerating}
            setIsGenerating={setIsGenerating}
            currentEntryId={currentEntryId}
            setCurrentEntryId={setCurrentEntryId}
            spendOverview={spendOverview}
            spendBySupplier={spendBySupplier}
            spendByDepartment={spendByDepartment}
            spendByBusinessUnit={spendByBusinessUnit}
            spendL3Insights={spendL3Insights}
            spendProcurementLevers={spendProcurementLevers}
            spendCostDrivers={spendCostDrivers}
            spendInflationOverview={spendInflationOverview}
            spendCommodityInflation={spendCommodityInflation}
            setSpendOverview={setSpendOverview}
            setSpendBySupplier={setSpendBySupplier}
            setSpendByBusinessUnit={setSpendByBusinessUnit}
            setSpendByDepartment={setSpendByDepartment}
            setSpendL3Insights={setSpendL3Insights}
            setSpendProcurementLevers={setSpendProcurementLevers}
            setSpendCostDrivers={setSpendCostDrivers}
            setSpendInflationOverview={setSpendInflationOverview}
            setSpendCommodityInflation={setSpendCommodityInflation}
          />
        )}
      </Box>
    </Box>
  );
}
